import { useState, useEffect, useRef, useCallback } from 'react';
import { SLIDE_PREVIEWS } from './PresentationBannerConstants';
import { WorkflowTimeline, DeckSummaryPanel } from './PresentationWorkflow';
import { BulkGeneratePanel } from './PresentationBulkGenerate';

interface Props { onSignUp: () => void; }

const TONES = ['Professional', 'Casual', 'Academic', 'Persuasive', 'Inspiring'];

export function GeneratePanel({ onSignUp }: Props) {
  const [mode, setMode]             = useState<'single' | 'bulk'>('single');
  const [topic, setTopic]           = useState('');
  const [slides, setSlides]         = useState(12);
  const [tone, setTone]             = useState('Professional');
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress]     = useState(0);
  const [done, setDone]             = useState(false);
  const [revealedSlides, setRevealedSlides] = useState(0);
  const [activeSlide, setActiveSlide]       = useState(0);
  const [isPlaying, setIsPlaying]           = useState(true);
  const [showKbHint, setShowKbHint]         = useState(false);
  const [showSummary, setShowSummary]       = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const carouselRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const visibleSlideCount = Math.min(slides, SLIDE_PREVIEWS.length);

  const statusMsg =
    progress < 25 ? 'Analyzing topic…' :
    progress < 50 ? 'Structuring outline…'   :
    progress < 75 ? 'Applying theme & layouts…' : 'Finalizing your deck…';

  const generate = () => {
    if (!topic.trim()) return;
    setGenerating(true); setDone(false); setProgress(0);
    setRevealedSlides(0); setActiveSlide(0); setIsPlaying(true); setShowSummary(false);
    let prog = 0;
    intervalRef.current = setInterval(() => {
      prog += Math.random() * 7 + 2.5;
      if (prog >= 100) {
        prog = 100;
        clearInterval(intervalRef.current!);
        setGenerating(false); setDone(true); setRevealedSlides(visibleSlideCount);
        setTimeout(() => setShowSummary(true), 600);
      }
      setProgress(prog);
      setRevealedSlides(Math.floor((prog / 100) * visibleSlideCount));
    }, 110);
  };

  const prevSlide = useCallback(() => setActiveSlide(p => (p - 1 + visibleSlideCount) % visibleSlideCount), [visibleSlideCount]);
  const nextSlide = useCallback(() => setActiveSlide(p => (p + 1) % visibleSlideCount), [visibleSlideCount]);
  const togglePlay = useCallback(() => setIsPlaying(v => !v), []);

  useEffect(() => {
    if (carouselRef.current) clearInterval(carouselRef.current);
    if (done && isPlaying) carouselRef.current = setInterval(nextSlide, 2200);
    return () => { if (carouselRef.current) clearInterval(carouselRef.current); };
  }, [done, isPlaying, nextSlide]);

  useEffect(() => {
    if (!done) return;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'ArrowLeft')  { e.preventDefault(); prevSlide(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); nextSlide(); }
      if (e.key === ' ')          { e.preventDefault(); togglePlay(); }
    };
    window.addEventListener('keydown', onKey);
    setShowKbHint(true);
    const t = setTimeout(() => setShowKbHint(false), 3500);
    return () => { window.removeEventListener('keydown', onKey); clearTimeout(t); };
  }, [done, prevSlide, nextSlide, togglePlay]);

  useEffect(() => () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (carouselRef.current) clearInterval(carouselRef.current);
  }, []);

  return (
    <>
      <div className="space-y-0">
        {/* Workflow Timeline */}
        <div className="-mx-6 -mt-6 mb-6">
          <WorkflowTimeline generating={generating && mode === 'single'} done={done && mode === 'single'} />
        </div>

        {/* Mode toggle */}
        <div className="flex items-center gap-1 p-1 bg-white/8 border border-white/12 rounded-xl mb-5 w-fit">
          <button onClick={() => setMode('single')} className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${mode === 'single' ? 'bg-white/15 text-white' : 'text-slate-500 hover:text-slate-300'}`}>
            <i className="ri-slideshow-line text-xs"></i>Single Deck
          </button>
          <button onClick={() => setMode('bulk')} className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${mode === 'bulk' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'text-slate-500 hover:text-slate-300'}`}>
            <i className="ri-stack-line text-xs"></i>Batch Generate
            <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-md font-bold">New</span>
          </button>
        </div>

        {mode === 'bulk' ? (
          <BulkGeneratePanel onSignUp={onSignUp} />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* ── Left: inputs ── */}
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 mb-2 uppercase tracking-wider">Presentation Topic</label>
                <textarea rows={2} value={topic} onChange={e => setTopic(e.target.value)}
                  placeholder='e.g. "Q3 Investor Update — SaaS Growth Metrics"'
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-rose-400/60 resize-none" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 mb-2 uppercase tracking-wider">Speaking Tone</label>
                <div className="flex flex-wrap gap-1.5">
                  {TONES.map(t => (
                    <button key={t} onClick={() => setTone(t)} className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${tone === t ? 'bg-rose-500 text-white' : 'bg-white/10 text-slate-400 hover:bg-white/15 hover:text-white'}`}>{t}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 mb-1 uppercase tracking-wider">Slide Count: {slides}</label>
                <input type="range" min={8} max={12} value={slides} onChange={e => setSlides(Number(e.target.value))} className="w-full accent-rose-500 cursor-pointer" />
                <div className="flex justify-between text-xs text-slate-500 mt-1"><span>8</span><span>12</span></div>
              </div>

              {generating && (
                <div className="bg-white/10 rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-rose-300 flex items-center gap-2"><i className="ri-loader-4-line animate-spin"></i>{statusMsg}</span>
                    <span className="text-xs font-bold text-white">{Math.round(Math.min(progress, 100))}%</span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-rose-500 to-pink-400 rounded-full transition-all duration-200" style={{ width: `${Math.min(progress, 100)}%` }} />
                  </div>
                </div>
              )}

              {done && (
                <div className="flex items-center justify-between gap-2 px-3 py-2 bg-emerald-500/15 border border-emerald-500/30 rounded-xl">
                  <div className="flex items-center gap-2">
                    <i className="ri-check-double-line text-emerald-400"></i>
                    <span className="text-xs font-bold text-emerald-300">Deck ready — {visibleSlideCount} slides · {tone}</span>
                  </div>
                  <button onClick={() => setShowSummary(v => !v)} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 border border-emerald-500/30">
                    <i className={`text-xs ${showSummary ? 'ri-layout-right-2-line' : 'ri-bar-chart-2-line'}`}></i>{showSummary ? 'Hide' : 'Summary'}
                  </button>
                </div>
              )}

              {!done ? (
                <button onClick={generate} disabled={generating || !topic.trim()} className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-rose-500 to-pink-500 disabled:opacity-40 text-white font-bold rounded-xl cursor-pointer hover:opacity-90 text-sm transition-all whitespace-nowrap w-full">
                  {generating ? <><i className="ri-loader-4-line animate-spin"></i>Generating…</> : <><i className="ri-magic-line"></i>Generate Presentation</>}
                </button>
              ) : (
                <button onClick={onSignUp} className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-rose-500 to-pink-500 text-white text-sm font-bold rounded-xl cursor-pointer hover:opacity-90 whitespace-nowrap">
                  <i className="ri-arrow-right-up-line text-sm"></i>Start for Free
                </button>
              )}
            </div>

            {/* ── Right: slide player ── */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Live Preview</span>
                {done && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">{activeSlide + 1} / {visibleSlideCount}</span>
                    <button onClick={togglePlay} className={`w-7 h-7 rounded-full flex items-center justify-center cursor-pointer transition-colors ${isPlaying ? 'bg-rose-500/20 border border-rose-500/40 text-rose-300' : 'bg-white/10 text-slate-400 hover:bg-white/15'}`}>
                      <i className={`text-xs ${isPlaying ? 'ri-pause-line' : 'ri-play-line'}`}></i>
                    </button>
                  </div>
                )}
              </div>

              {/* Main display */}
              <div className="relative aspect-video rounded-2xl overflow-hidden border border-white/15 bg-white/5 flex items-center justify-center">
                {!generating && !done && (
                  <div className="flex flex-col items-center gap-3 text-slate-600">
                    <div className="w-14 h-14 rounded-2xl border-2 border-dashed border-white/15 flex items-center justify-center">
                      <i className="ri-slideshow-line text-2xl"></i>
                    </div>
                    <p className="text-xs text-slate-600 text-center leading-relaxed">Type a topic and hit<br /><strong className="text-slate-400">Generate</strong> to preview</p>
                  </div>
                )}

                {(generating || done) && revealedSlides > 0 && (
                  <>
                    <img key={activeSlide}
                      src={`https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg$%7BencodeURIComponent%28SLIDE_PREVIEWS%5BactiveSlide%5D.query%29%7D&width=640&height=360&seq=${SLIDE_PREVIEWS[activeSlide].seq}&orientation=landscape`}
                      alt={SLIDE_PREVIEWS[activeSlide].type} className="w-full h-full object-cover transition-opacity duration-500" />
                    <div className="absolute bottom-0 left-0 right-0 px-3 py-2 bg-gradient-to-t from-black/60 to-transparent">
                      <p className="text-xs font-bold text-white">{SLIDE_PREVIEWS[activeSlide].type}</p>
                    </div>
                    <div className="absolute top-2.5 right-2.5 px-2 py-1 bg-black/50 backdrop-blur-sm rounded-lg">
                      <span className="text-xs font-bold text-white">{activeSlide + 1}/{visibleSlideCount}</span>
                    </div>
                    {done && isPlaying && (
                      <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5 px-2 py-1 bg-black/50 backdrop-blur-sm rounded-lg">
                        <div className="flex gap-0.5 items-end h-3">
                          {[0, 1, 2].map(i => <div key={i} className="w-0.5 bg-rose-400 rounded-full animate-pulse" style={{ height: `${8 + i * 4}px`, animationDelay: `${i * 0.15}s` }}></div>)}
                        </div>
                        <span className="text-xs text-rose-300 font-semibold">Playing</span>
                      </div>
                    )}
                    {done && !isPlaying && (
                      <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5 px-2 py-1 bg-black/50 backdrop-blur-sm rounded-lg">
                        <i className="ri-pause-line text-slate-400 text-xs"></i>
                        <span className="text-xs text-slate-400 font-semibold">Paused</span>
                      </div>
                    )}
                    {generating && (
                      <div className="absolute inset-0 overflow-hidden pointer-events-none">
                        <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.6s_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                      </div>
                    )}
                    {done && (
                      <>
                        <button onClick={prevSlide} className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center cursor-pointer hover:bg-black/70 transition-colors">
                          <i className="ri-arrow-left-s-line text-white text-sm"></i>
                        </button>
                        <button onClick={nextSlide} className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center cursor-pointer hover:bg-black/70 transition-colors">
                          <i className="ri-arrow-right-s-line text-white text-sm"></i>
                        </button>
                      </>
                    )}
                  </>
                )}

                {generating && revealedSlides === 0 && (
                  <div className="absolute inset-0 bg-white/5">
                    <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.6s_infinite] bg-gradient-to-r from-transparent via-white/15 to-transparent" />
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin text-2xl text-rose-400"></i>
                      <p className="text-xs text-slate-500">Building slide 1…</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Filmstrip */}
              <div className="flex gap-1.5 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
                {SLIDE_PREVIEWS.slice(0, visibleSlideCount).map((s, i) => {
                  const revealed = i < revealedSlides;
                  const isActive = i === activeSlide;
                  return (
                    <button key={s.seq} onClick={() => { if (revealed && done) { setActiveSlide(i); setIsPlaying(false); } }}
                      className={`relative flex-shrink-0 w-16 h-10 rounded-lg overflow-hidden border-2 transition-all duration-300 ${isActive && revealed ? 'border-rose-400/60 scale-105' : revealed ? 'border-white/20 hover:border-white/40 cursor-pointer' : 'border-white/10 cursor-default'}`}>
                      {revealed ? (
                        <img src={`https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28s.query%29%7D&width=128&height=80&seq=${s.seq}-thumb&orientation=landscape`} alt={s.type} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-white/5 relative overflow-hidden">
                          <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.6s_infinite] bg-gradient-to-r from-transparent via-white/12 to-transparent" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Dot nav */}
              {(generating || done) && (
                <div className="flex items-center gap-1 justify-center">
                  {SLIDE_PREVIEWS.slice(0, visibleSlideCount).map((_, i) => (
                    <div key={i} onClick={() => { if (done) { setActiveSlide(i); setIsPlaying(false); } }}
                      className={`rounded-full transition-all duration-300 ${i === activeSlide ? 'w-4 h-1.5 bg-rose-400' : i < revealedSlides ? 'w-1.5 h-1.5 bg-white/30 cursor-pointer hover:bg-white/50' : 'w-1.5 h-1.5 bg-white/10'}`} />
                  ))}
                </div>
              )}

              {/* Keyboard hints */}
              {done && (
                <div className={`flex items-center justify-center gap-3 transition-all duration-500 ${showKbHint ? 'opacity-100' : 'opacity-40 hover:opacity-70'}`}>
                  <span className="text-xs text-slate-600">Keyboard:</span>
                  {[{ key: '←', label: 'Prev' }, { key: '→', label: 'Next' }, { key: 'Space', label: isPlaying ? 'Pause' : 'Play' }].map(k => (
                    <div key={k.key} className="flex items-center gap-1">
                      <kbd className="px-1.5 py-0.5 bg-white/10 border border-white/15 rounded text-xs font-mono text-slate-400 leading-none">{k.key}</kbd>
                      <span className="text-xs text-slate-600">{k.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Deck summary panel (single mode only) */}
      {mode === 'single' && (
        <DeckSummaryPanel topic={topic} slideCount={visibleSlideCount} tone={tone} visible={showSummary} onClose={() => setShowSummary(false)} />
      )}
    </>
  );
}
